import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Home, CalendarDays, Euro } from "lucide-react";
import { SocialSettings } from "@/components/SocialSettings";
import { Contacts } from "@/components/Contacts";
import { SmsSettings } from "@/components/SmsSettings";
import { InviteMember } from "@/components/InviteMember";
import { HomeSettings } from "@/components/HomeSettings";
import { RegionSettings } from "@/components/RegionSettings";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
    setStats(s.data); setUsers(u.data);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.role) { navigate("/connexion"); return; }
    if (user.role !== "admin") { navigate("/mes-sejours"); return; }
    load();
  }, [user, loading, navigate, load]);

  const changeRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, null, { params: { role } }); toast.success("Rôle mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!stats) return <div className="min-h-screen bg-background"><Header /></div>;

  const cards = [
    { icon: Users, label: "Utilisateurs", value: stats.users },
    { icon: Home, label: "Logements", value: stats.listings },
    { icon: CalendarDays, label: "Réservations", value: stats.bookings },
    { icon: Euro, label: "Revenus", value: `${stats.revenue} €` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-serif text-4xl text-olive">Administration</h1>
        <p className="text-muted-foreground mt-1 mb-8">Vue d'ensemble de la plateforme</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="p-5 rounded-2xl bg-white border border-border" data-testid={`admin-stat-${c.label}`}>
              <c.icon className="w-6 h-6 text-rose-brand" />
              <p className="text-2xl font-bold text-olive mt-3">{c.value}</p>
              <p className="text-sm text-muted-foreground">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10"><InviteMember onInvited={load} /></div>
        <div className="mt-10"><HomeSettings /></div>
        <div className="mt-10"><RegionSettings /></div>
        <div className="mt-10"><SocialSettings /></div>
        <div className="mt-10"><SmsSettings /></div>
        <div className="mt-10"><Contacts /></div>

        <h2 className="font-serif text-2xl text-olive mt-10 mb-4">Utilisateurs</h2>
        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-users-table">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0">
              <div>
                <p className="font-semibold text-olive">{u.name}</p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
              <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                <SelectTrigger className="w-40" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="traveler">Voyageur</SelectItem>
                  <SelectItem value="host">Hôte / Équipe</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
